/**
 * Who a customer usually searches for, so a search form opens with them
 * already filled in.
 *
 * The early search (src/lib/searchPrefetch.js) starts as soon as a form holds
 * a complete search. A customer who searches for two, on a form that opens at
 * one adult, changes the travelers last, right before pressing Search, and that
 * throws the early search away. Opening at their usual party lets it run the
 * whole time they're on the form.
 *
 * The usual party is the one searched most often in the last few searches on
 * this device; a tie goes to the most recent. Each search is kept the way the
 * results URL carries it (src/utils/passengerTypes.js), so a remembered party
 * reads back exactly like a search from the home page. Storage can be missing
 * or throw (private windows, blocked site data), and then forms open at one
 * adult as before.
 */
import { readPassengerParams, writePassengerParams } from '@/utils/passengerTypes';

const STORAGE_KEY = 'defera.searchTravelers';
/** How many recent searches decide the usual party. */
export const USUAL_TRAVELERS_WINDOW = 5;

const browserStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const asQuery = (passengers) => writePassengerParams(new URLSearchParams(), passengers).toString();

const readHistory = (storage) => {
  try {
    const list = JSON.parse(storage?.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((q) => typeof q === 'string') : [];
  } catch {
    return [];
  }
};

/** Note the travelers a customer just searched for (most recent first). */
export function rememberSearchTravelers(passengers, { storage = browserStorage() } = {}) {
  if (!storage || !Array.isArray(passengers) || passengers.length === 0) return;
  const history = [asQuery(passengers), ...readHistory(storage)].slice(0, USUAL_TRAVELERS_WINDOW);
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // A full or blocked store only means the form opens at one adult.
  }
}

/** The party this customer searches most, or null with no history. */
export function usualSearchTravelers({ storage = browserStorage() } = {}) {
  const history = readHistory(storage);
  if (history.length === 0) return null;
  const counts = new Map();
  for (const q of history) counts.set(q, (counts.get(q) || 0) + 1);
  // history is newest first, so the first to reach the top count is the latest.
  const top = Math.max(...counts.values());
  const usual = history.find((q) => counts.get(q) === top);
  return readPassengerParams(new URLSearchParams(usual));
}

/** A form's opening travelers: the usual party, else one adult. */
export function initialSearchTravelers(options) {
  return usualSearchTravelers(options) || [{ type: 'adult' }];
}
